import { Component, ReactNode, useId } from "react";
import { container } from '../../context/module';
import { MetricsService } from '../../services/metrics.service';
import { MetricEventName } from '../../types/metric-events';
import { Subscription } from "rxjs";
import { withRequestIdleCallbackAsync } from "../../annotations/request-idle-callback.annotation";
import { gifEstimateStore } from "../../utils/gif-estimate.store";
import { FileUpload } from "../../models/file-upload";
import { UploadInProgressFile } from "../../models/upload-in-progress-file";
import Checkbox from "../ui/checkbox/checkbox.component";
import Dropdown from "../ui/dropdown/dropdown";
import Seekbar from "../ui/seekbar/seekbar.component";
import styles from './convert-options.component.module.scss';
import { hasLongWord } from './option-label-layout';
import { ConvertOptionsContextV2, ConvertOptionsSummary } from "./convert-options.context";
import { BooleanViewState, DropdownOptionViewState, DropdownViewState, GroupViewState, Note, OptionLevel, OptionViewState, RangeViewState, RootOptionViewState, SelectableOptionViewState, SubgroupViewState, TextInputDropdownViewState, TextInputViewState, ValueViewState, ViewState } from "./convert-options.view-states";
import { isHidden } from "./models/option-requirement";
import { DropdownInputComponent, DropdownInputValueChangeEvent } from "./widgets/dropdown-input.component";
import { TextInputComponent, TextInputValueChangeEvent } from "./widgets/text-input.component";
/**
 * A hydration-stable id per mounted group (React `useId`), so two panels over the same tree — the
 * converter page and the re-edit modal on /compress-pdf — never share a mode-note region id.
 */
function WithInstanceId({ children }: { children: (id: string) => ReactNode }) {
    const id = useId();
    return <>{children(id)}</>;
}

/** A note whose text can change while its mode stays selected (a textFn, or an interpolated template). */
function isLiveNote(note: Note): boolean {
    return !!note.textFn || (typeof note.text === 'string' && note.text.includes('${'));
}

interface ConvertOptionsComponentState {
    results: any;
}

interface ConvertOptionsComponentProps {
    convertOptionsContext: ConvertOptionsContextV2;
    fileUpload?: FileUpload;
    uploadInProgressFiles: UploadInProgressFile<any>[];
    onOptionChange: (data: ConvertOptionsSummary) => void;
    showAdvanced: boolean;
    path: string;
    /**
     * Render only the top-level groups this returns true for. Default: all of them.
     *
     * The Studio's export sheet mounts the page's compression group and nothing else (STUDIO_DESIGN
     * §6.2), and mounting means MOUNTING: the labels, the hidden rules, the buttons-vs-dropdown
     * switch above six visible options (`renderGroupOptionsSection`) and the defaults are the
     * converter's, regenerated by xconfig, and a hand-built copy would be a second source of truth
     * for a tree we do not own.
     *
     * Additive and optional, so every existing call site renders exactly what it rendered before:
     * the filter below short-circuits on the absent prop, and `convert-options.group-filter.spec`
     * pins the two renders as byte-identical HTML.
     *
     * It is ANDed with the advanced-level and hidden filters, never a replacement for them — a
     * caller can narrow what the page decided to show, never widen it. It is applied first only
     * because `isHidden` walks the sibling tree per group and there is no reason to walk twelve
     * of thirteen to throw them away.
     */
    groupFilter?: (group: GroupViewState) => boolean;
    /**
     * FIX-3b (UX1-07): the setting "Make it smaller" pre-selected in the Re-edit modal. The top-level groups in `groupIds`
     * get a highlight ring and `data-suggested="true"`; inside them, the control that writes `propertyName` (the selected
     * choice of a group, a sub-option row or a dropdown) gets `data-suggested-choice="true"` -- what the modal scrolls
     * into view. Only rendered groups can be marked, so a hidden candidate is simply ignored. Optional: absent, every
     * group's markup is exactly what it was.
     */
    highlight?: { groupIds: readonly string[]; propertyName?: string };
    /**
     * Whether THIS instance reports the option-change metric. Default true — every existing call
     * site is unchanged, because the flag is only read as an explicit `false`.
     *
     * It exists because two instances can share one context. The Studio's export sheet mounts this
     * component twice over one `ConvertOptionsContextV2` (the Size group and the quality group,
     * each narrowed by `groupFilter`), and both subscribe to the same `modelChangeObserver`. The
     * doubled `onOptionChange` is harmless — each instance is handed the WHOLE summary, so the
     * callback is idempotent — but the metric is not: each instance keeps its own
     * `prevSummarySnapshot`, so ONE user change increments `optionChangesThisPage` twice AND
     * double-samples the 1:10 `OPTION_CHANGED` event, which carries `page: location.pathname` and
     * therefore lands in a real converter page's statistics (Studio final review, question 10).
     *
     * Pass `false` from every instance but one wherever a context is mounted more than once.
     */
    countOptionChanges?: boolean;
}

export class ConvertOptionsComponentV2 extends Component<ConvertOptionsComponentProps, ConvertOptionsComponentState> {

    private model: RootOptionViewState;
    private modelChangeSubscription?: Subscription;
    private unsubscribeGifEstimates?: () => void;

    constructor(props) {
        super(props);
        this.model = this.props.convertOptionsContext.getModel() as any;

        // This calculates the initial view state including hidden options (ensureContextState)
        // preventing CLS by ensuring the first render matches the final state.
        const initialResults = this.model ? this.props.convertOptionsContext.getSummary() : null;

        this.state = {
            results: initialResults
        };
    }

    private prevSummarySnapshot: { [key: string]: string } | null = null;

    /**
     * "Which knobs do users actually touch" (sweep P2). The model observer emits the
     * whole summary, so changed keys come from a shallow diff against the previous
     * emission. Sampled 1:10 client-side; the always-counted side effect feeds
     * MetricsService.optionChangesThisPage so CONVERT_START can report it exactly.
     */
    private emitOptionChangeMetric(summary: { [key: string]: any }) {
        try {
            const snap: { [key: string]: string } = {};
            for (const k of Object.keys(summary || {})) snap[k] = JSON.stringify(summary[k]);
            const prev = this.prevSummarySnapshot;
            this.prevSummarySnapshot = snap;
            if (!prev) return; // first emission is the baseline, not a user change
            const metrics = container.get(MetricsService);
            for (const k of Object.keys(snap)) {
                if (prev[k] === snap[k]) continue;
                metrics.optionChangesThisPage += 1;
                if (Math.random() < 0.1) {
                    metrics.emit(MetricEventName.OPTION_CHANGED, {
                        key: k,
                        page: typeof location !== 'undefined' ? location.pathname : '',
                    });
                }
            }
        } catch (e) { /* metrics must never break the options panel */ }
    }

    componentDidMount(): void {
        this.updateLikelyStaleResult();

        this.modelChangeSubscription = this.props.convertOptionsContext.modelChangeObserver.subscribe({
            next: (obj) => {
                this.updateLikelyStaleResult();
                // A second instance over the same context would report the same change again.
                if (this.props.countOptionChanges !== false) this.emitOptionChangeMetric(obj);
                this.props.onOptionChange(obj);
            }
        });
        // Estimates land asynchronously (Stage 0/1/2) and only re-derive NOTES,
        // never values — re-render so the textFn notes read the fresh records.
        this.unsubscribeGifEstimates = gifEstimateStore.subscribe(() => this.forceUpdate());
    }

    componentWillUnmount(): void {
        this.modelChangeSubscription?.unsubscribe();
        this.unsubscribeGifEstimates?.();
    }

    componentDidUpdate(prevProps: Readonly<ConvertOptionsComponentProps>, prevState: Readonly<ConvertOptionsComponentState>, snapshot?: any): void {
        this.updateLikelyStaleResult();
    }

    updateLikelyStaleResult() {
        if (this.model) {
            // Trigger restoration logic (Mutates model if context changed)
            // We use getSummary() because it effectively calls summarize() with the current view results
            this.props.convertOptionsContext.getSummary();

            const currentResults = this.model.getResultForView();
            // Simple JSON stringify comparison to avoid infinite loop
            if (JSON.stringify(currentResults) !== JSON.stringify(this.state.results)) {
                this.setState({
                    results: currentResults
                });
            }
        }
    }

    render(): ReactNode {
        return <div>
            {
                this.model && this.model.options
                    .filter(it => it instanceof GroupViewState)
                    .filter(it => !this.props.groupFilter || this.props.groupFilter(it as GroupViewState))
                    .filter(currentVS => {
                        if (!this.props.showAdvanced && currentVS.optionLevel === OptionLevel.Advanced) {
                            return false;
                        }

                        return !currentVS.isHidden(this.state.results, (propertyName: string) => {
                            const viewStates = this.props.convertOptionsContext.getViewStates(propertyName);
                            if (!viewStates) {
                                return false;
                            }
                            const shouldBeHidden = viewStates.filter(it => {
                                return it instanceof RootOptionViewState ||
                                    it instanceof GroupViewState ||
                                    it instanceof SubgroupViewState;
                            }).filter(it => it != currentVS).every(it => {
                                let vs: ViewState = it;
                                if (vs instanceof RootOptionViewState) {
                                    return false;
                                }
                                while (!(vs instanceof RootOptionViewState)) {
                                    if (vs != currentVS && (vs as any)?.isHidden && (vs as any)?.isHidden(this.state.results)) {
                                        return true;
                                    }
                                    vs = vs.parent;
                                }
                                return false
                            });
                            return shouldBeHidden;
                        })
                    })
                    .map(subgroup => {
                        const content = this.renderGroup(subgroup);
                        if (!content) {
                            return null;
                        }
                        const suggested = !!this.props.highlight?.groupIds.includes(String(subgroup.id));
                        return <div className={suggested ? `group ${styles['suggested-group']}` : 'group'} data-suggested={suggested ? 'true' : undefined}
                            data-testid={`convert-options-group-${subgroup.id}`} key={`group-parent-${subgroup.id}`}>
                            {
                                content
                            }
                        </div>
                    })
            }
        </div>
    }

    renderGroup(model: GroupViewState) {
        const selectableGroupOptions = model.options;
        const visibleOptions = selectableGroupOptions
            .filter(it => !it.isHidden(this.state.results))
            .filter(it => this.props.showAdvanced || it.optionLevel !== OptionLevel.Advanced);

        if (visibleOptions.length === 0) {
            return null;
        }

        if (visibleOptions.length === 1) {
            const singleOption = visibleOptions[0];
            const hasChildren = singleOption.options && singleOption.options.length > 0;

            if (!hasChildren) {
                return null;
            }

            const selectedSubgroupOption = singleOption.options?.filter(it => it instanceof SubgroupViewState);
            return <div className={styles['group']}>
                <div className={styles['header']}>{model.name}</div>
                {this.renderNotes(model.notes, styles['category-notes'])}
                <div className={styles['category_option_selections-and-sub-options']}>
                    <div>
                        {this.renderNotes(singleOption.notes, styles['option-notes'])}
                        {
                            this.renderSubgroup(selectedSubgroupOption)
                        }
                    </div>
                </div>
            </div>
        }

        const selectedOption = selectableGroupOptions
            ?.find(it => it.selected)
            ?.options ?? selectableGroupOptions[0].options;

        const selectedSubgroupOption =
            selectedOption?.filter(it => it instanceof SubgroupViewState)
        const subgroups = this.renderSubgroup(selectedSubgroupOption);
        // Mode notes get a region only in groups that HAVE mode notes, so every other group's
        // markup and spacing is exactly what it was.
        const hasModeNotes = selectableGroupOptions.some(it => it.notes && it.notes.length > 0);
        // The region is aria-live only when every mode note of the group is STATIC: then its content
        // changes exactly when the selection does. A group with a note that rewrites itself while
        // its mode stays selected (GIF_MAX_FILE_SIZE's live estimate) would announce every update,
        // so it gets the region (and aria-describedby) without aria-live.
        const liveRegion = hasModeNotes && !selectableGroupOptions.some(it => (it.notes ?? []).some(isLiveNote));
        const selectedNotes = this.renderNotes(
            selectableGroupOptions.find(it => it.selected)?.notes,
            styles['option-notes'],
        );
        const groupNotes = this.renderNotes(model.notes, styles['category-notes']);
        const groupNoteShown = Array.isArray(groupNotes) && groupNotes.some(Boolean);
        return <WithInstanceId>{(instanceId) => {
        const notesId = ConvertOptionsComponentV2.modeNotesId(model, instanceId);
        return <div className={styles['group']}>
            <div className={styles['header']}>{model.name}</div>
            <div className={styles['category_option_selections-and-sub-options']}>
                <div>
                    {
                        this.renderGroupOptionsSection(selectableGroupOptions, model, hasModeNotes ? notesId : undefined)
                    }
                </div>
                {groupNotes}
                {
                    // The empty wrapper is kept where it always was (it holds the group's trailing
                    // gap); it is dropped only above a mode-note region, where it was an empty
                    // 12px gap between the two notes.
                    (subgroups || !hasModeNotes) && <div>{subgroups}</div>
                }
                {
                    // A note on the selected MODE describes the outcome of every
                    // control the mode reveals, so it renders AFTER them — the
                    // house convention everywhere else (a note follows the thing
                    // it explains). The region is always mounted and the selected choice
                    // points at it (aria-describedby); where its notes are static it is
                    // polite-live, so a screen reader hears the new note on a selection change.
                    hasModeNotes
                        ? <div id={notesId} aria-live={liveRegion ? 'polite' : undefined} data-testid={`convert-options-mode-notes-${model.id}`}
                            // Under a group note the two boxes sit one row-gap apart, not row-gap plus
                            // the alert's own bottom margin.
                            className={groupNoteShown ? styles['mode-notes-after-note'] : undefined}>{selectedNotes}</div>
                        : selectedNotes
                }
            </div>
        </div>
        }}</WithInstanceId>
    }

    /** The id of a group's mode-note region (see `renderGroup`): group id + a per-instance suffix. */
    static modeNotesId(model: GroupViewState, instanceId: string): string {
        return `xc-mode-notes-${String(model.id)}-${instanceId.replace(/[^A-Za-z0-9_-]/g, '')}`;
    }

    /** `aria-describedby` for the SELECTED choice when it has a note to point at. */
    private modeNotesDescribedBy(model: SelectableOptionViewState, notesId?: string): string | undefined {
        if (!notesId || !model.selected || !model.notes || model.notes.length === 0) return undefined;
        const visible = model.notes.some(note => !(note.hidden && isHidden(note.hidden, this.state.results)));
        return visible ? notesId : undefined;
    }

    renderGroupOptionsSection(selectableGroupOptions: SelectableOptionViewState[], model: GroupViewState, notesId?: string) {
        const visibleOptions = selectableGroupOptions
            .filter(it => !it.isHidden(this.state.results))
            .filter(it => this.props.showAdvanced || it.optionLevel !== OptionLevel.Advanced);

        // Buttons up to the group's own limit (xconfig `maxButtonOptions`, default 6), then a dropdown.
        if (visibleOptions.length > (model.maxButtonOptions ?? 6)) {
            const dropdown = model;
            const values = visibleOptions;

            if (values.length === 0) {
                return null;
            }

            const selectedIsValid = values.find(it => it.selected);
            if (!selectedIsValid) {
                values[0].selected = true;
            }
            const selectedOption = dropdown.selected;
            return <div className={styles['category_option_selections-sub-options']}>
                <div className={`${styles['category_option_selections-sub-option']}`} data-suggested-choice={this.suggestedChoiceAttr(model.propertyNames)}>
                    <div className={styles['controller-container']}>
                        <Dropdown
                            key={`${model.id}-selectable-options`}
                            initialValue={selectedOption}
                            values={values}
                            label={null}
                            ariaLabel={model.name}
                            valueFormatter={(v) =>
                                v && v.name
                            }
                            hasTooltip={(v) => false}
                            getTooltip={(v) => "n/a"}
                            emitInitialValueOnInit={true}
                            searchable={true}
                            onSelect={(selectedValue, forced) =>
                                this.setSelectableOptionValue(
                                    dropdown,
                                    selectedValue
                                )
                            }
                        >
                        </Dropdown>
                    </div>
                </div>

            </div>
        }

        if (visibleOptions.length === 0) {
            return null;
        }

        const selectedOption = selectableGroupOptions.find(it => it.selected);

        return <div className={styles['category_option_selections-container']}>
            <div className={styles['category_option_selections']}>
                {visibleOptions
                    .map(child => {
                        return this.renderGroupOption(child, model, notesId);
                    })
                }
            </div>
        </div>
    }

    renderGroupOption(model: SelectableOptionViewState, parent: GroupViewState, notesId?: string) {
        // UX2-09: a label with a long word spans the row where a half row is too narrow for it.
        const cellClass = hasLongWord(model.name)
            ? `${styles['category_option_selection']} ${styles['long-label']}`
            : styles['category_option_selection'];
        return <div className={cellClass} key={`group-option-${model.id}`}>
            <button
                className={`btn btn-light btn-gray-outline x-btn-svg x-btn-stacked ${styles[model.selected ? 'selected' : 'not-selected']}`}
                aria-describedby={this.modeNotesDescribedBy(model, notesId)}
                data-suggested-choice={model.selected ? this.suggestedChoiceAttr(parent.propertyNames) : undefined}
                onClick={e => { this.setSelectableOptionValue(parent, model) }}>
                {model.iconPath && <img src={model.iconPath} />}
                {model.name}
            </button>
        </div>
    }

    /** FIX-3b (UX1-07): `"true"` on the control that writes the highlighted property, else no attribute at all. */
    private suggestedChoiceAttr(propertyNames: string[] | undefined): 'true' | undefined {
        const key = this.props.highlight?.propertyName;
        return key && Array.isArray(propertyNames) && propertyNames.includes(key) ? 'true' : undefined;
    }

    renderSubgroup(model: SubgroupViewState[]) {
        const visibleSubgroups = model.filter(it => !it.isHidden(this.state.results));
        if (visibleSubgroups.length === 0) {
            return null;
        }
        return <div className={styles['category_option_selections-sub-options']}>
            {
                visibleSubgroups.map(it => {
                    return <div key={it.uuid}>
                        <div className={`${styles['category_option_selections-sub-option']}`} data-suggested-choice={this.suggestedChoiceAttr(it.propertyNames)}>
                            <div className={`${styles['label']} text-flex-vertical-center`}>
                                {it.name}
                            </div>
                            <div className={styles['controller-container']}>
                                <div>
                                    {
                                        it.options
                                            ?.filter(i => {
                                                return !(i instanceof GroupViewState || i instanceof SelectableOptionViewState)
                                            })
                                            ?.map(child => {
                                                return this.renderNonGroupOption(child, it);
                                            })
                                    }
                                </div>
                            </div>
                        </div>
                        {
                            this.renderNotes(it.notes, styles['suboption-notes'])
                        }
                    </div>
                })
            }

        </div>
    }

    renderNotes(notes: Note[], className: string) {
        if (!notes || notes.length === 0) return null;
        return notes.map((note, index) => {
            if (note.hidden && isHidden(note.hidden, this.state.results)) {
                return null;
            }
            if (note.hiddenFn && this.evalInContext(note.hiddenFn)) {
                return null;
            }
            return <div key={index}>
                {note.text && <div className={`${note.textClassName} ${className}`}
                    dangerouslySetInnerHTML={{
                        __html: this.modeNoteCue(note, className) + this.evalInContext.call(
                            this,
                            note.text
                        )
                    }}>
                </div>}
                {note.textFn && Note.fn[note.textFn] && (() => {
                    // A note function returning nothing means "no note right now" —
                    // don't render an empty alert shell.
                    const html = Note.fn[note.textFn]({
                        results: this.state.results,
                        uploadInProgressFiles: this.props.uploadInProgressFiles,
                        context: this.props.convertOptionsContext,
                        fileUpload: this.props.fileUpload,
                        showAdvanced: this.props.showAdvanced,
                    });
                    if (!html) return null;
                    return <div className={`${note.textClassName} ${className}`}
                        dangerouslySetInnerHTML={{ __html: html }}>
                    </div>;
                })()}
            </div>
        });
    }

    /**
     * A warning must not be told apart by colour alone, so a mode note whose xconfig `severity` is
     * `warning` leads with an icon and a screen-reader "Warning:". Driven ONLY by `severity`, never
     * by `textClassName` (that is styling: the frame-selection notes are alert-warning and are not
     * warnings). Mode notes with `text` only; a textFn note brings its own markup.
     */
    modeNoteCue(note: Note, className: string): string {
        if (className !== styles['option-notes']) return '';
        if (note.severity !== 'warning') return '';
        return '<span aria-hidden="true">&#9888;&#xFE0E; </span><span class="sr-only">Warning: </span>';
    }

    evalInContext(value) {
        const results = this.state.results;
        const uploadInProgressFiles = this.props.uploadInProgressFiles;
        const context = this.props.convertOptionsContext;
        const fileUpload = this.props.fileUpload;
        const showAdvanced = this.props.showAdvanced;
        const path = this.props.path;
        return eval(value);
    }

    setSelectableOptionValue(parent: GroupViewState, model: SelectableOptionViewState) {
        parent.updateValue(model);
        this.props.convertOptionsContext.emitModelChanged();
    }

    @withRequestIdleCallbackAsync
    async setCheckboxValue(value: boolean, model: BooleanViewState) {
        model.updateValue(value);
        this.props.convertOptionsContext.emitModelChanged();
    }

    setSeekbarValue(value: number, model: RangeViewState) {
        model.updateValue(value);
        this.props.convertOptionsContext.emitModelChanged();
    }

    setDropdownInputValue(value: DropdownInputValueChangeEvent, model: TextInputDropdownViewState): void {
        model.updateValue(
            value.inputValue,
            value.dropdownValue
        );
        this.props.convertOptionsContext.emitModelChanged();
    }

    setTextInputValue(value: TextInputValueChangeEvent, model: TextInputViewState): void {
        model.updateValue(
            value.inputValue
        );
        this.props.convertOptionsContext.emitModelChanged();
    }

    setDropdownValue(
        value: DropdownViewState,
        selectedValue: DropdownOptionViewState,
        force: Boolean
    ): void {
        value.updateValue(selectedValue);
        this.props.convertOptionsContext.emitModelChanged();
    }

    renderNonGroupOption(model: OptionViewState | ValueViewState, patent: SubgroupViewState) {
        // UXFU2-01: most controls in a subgroup carry no name of their own; the subgroup's visible label (`renderSubgroup`
        // draws `patent.name` beside them) is what they set, so a screen reader hears "Compression level: 6", not "6".
        const groupName = patent?.name || undefined;
        if (model instanceof BooleanViewState) {
            return <Checkbox
                key={`${patent.id}-${model.id}`}
                initialValue={model.value}
                ariaLabel={model?.name || groupName}
                onChange={(value) => {
                    this.setCheckboxValue(value, model);
                }}
                emitInitialValueOnInit={true}
            ></Checkbox>
        } else if (model instanceof RangeViewState) {
            return <Seekbar
                key={`${patent.id}-${model.id}`}
                step={model.step ?? 1}
                min={model.min}
                max={model.max}
                start={model.value}
                ariaLabel={groupName}
                // UXM-01: "File size (%)", "Max % of original", "Quality Percentage" -- the value is a percent, so say so.
                valueText={groupName && /%|percent/i.test(groupName) ? (v) => `${v}%` : undefined}
                onChange={(value) => {
                    this.setSeekbarValue(value, model)
                }}
                emitInitialValueOnInit={true}
            ></Seekbar>
        } else if (model instanceof TextInputDropdownViewState) {
            const dropdown = model.dropdown;
            const values = dropdown.options.filter(it => !it.isHidden(this.state.results));
            const selectedIsValid = values.find(it => it.selected);
            if (!selectedIsValid) {
                dropdown.selected = values[0];
            }
            return <DropdownInputComponent
                key={`${patent.id}-${model.id}`}
                label={model.name}
                ariaLabel={groupName}
                values={values}
                inputValue={model.value}
                dropdownValue={dropdown.selected}
                valueFormatter={(a) =>
                    a && a.name
                }
                valueChanged={(value) => this.setDropdownInputValue(value, model)} >
            </DropdownInputComponent>
        } else if (model instanceof DropdownViewState) {
            const dropdown = model;
            const values = dropdown.options.filter(it => !it.isHidden(this.state.results));
            const selectedIsValid = values.find(it => it.selected);
            if (!selectedIsValid) {
                dropdown.selected = values[0];
            }
            const selectedOption = dropdown.selected;
            return <div key={`${patent.id}-${model.id}`}>
                <Dropdown
                    initialValue={selectedOption}
                    values={values}
                    label={dropdown.name}
                    ariaLabel={groupName}
                    valueFormatter={(v) =>
                        v && v.name
                    }
                    hasTooltip={(v) => false}
                    getTooltip={(v) => "n/a"}
                    emitInitialValueOnInit={true}
                    onSelect={(selectedValue, forced) =>
                        this.setDropdownValue(
                            dropdown, selectedValue, forced
                        )
                    }
                >
                </Dropdown>
            </div>
        } else if (model instanceof TextInputViewState) {
            return <TextInputComponent
                key={`${patent.id}-${model.id}`}
                label={model.name}
                ariaLabel={groupName}
                inputValue={model.value}
                valueChanged={(value) => this.setTextInputValue(value, model)}
            ></TextInputComponent>
        }
        return <div>{/* Orphan: {model.id} */}</div>
    }
}
